/**
 * IoT/Hardware Flow Definition
 * 
 * Defines the complete 13-step IoT wizard flow:
 * Steps 1-4: Shared with all flows (Intent, Workspace, Project, Import)
 * Steps 5-13: IoT-specific (Mode, Agent, Device, Setup, Test, Validation, Generate, Run, Summary)
 */

// Import step components
import Step1Intent from '../components/wizard/steps/Step1Intent';
import Step2Workspace from '../components/wizard/steps/Step2Workspace';
import Step3Project from '../components/wizard/steps/Step3Project';
import Step4TestSource from '../components/wizard/steps/Step4TestSource';

// IoT-specific steps
import Step5IotMode from '../components/wizard/steps/iot/Step5IotMode';
import Step6IotLabAgent from '../components/wizard/steps/iot/Step6IotLabAgent';
import Step7IotDevicePairing from '../components/wizard/steps/iot/Step7IotDevicePairing';
import Step8IotInteractionSetup from '../components/wizard/steps/iot/Step8IotInteractionSetup';
import Step9IotCreateTest from '../components/wizard/steps/iot/Step9IotCreateTest';
import Step10IotValidation from '../components/wizard/steps/iot/Step10IotValidation';
import Step11IotGenerate from '../components/wizard/steps/iot/Step11IotGenerate';
import Step12IotRun from '../components/wizard/steps/iot/Step12IotRun';
import Step13IotSummary from '../components/wizard/steps/iot/Step13IotSummary';

/**
 * IoT Flow Definition
 */
const iotFlow = {
  flowId: 'iot_hardware',
  appType: 'iot_hardware',
  totalSteps: 13,
  
  /**
   * Step definitions
   * Each step includes: id, label, component, and optional gating logic
   */
  steps: [
    {
      id: 1,
      label: 'Intent',
      component: Step1Intent,
      isPublic: true,
      description: 'Select your testing goal'
    },
    {
      id: 2,
      label: 'Workspace',
      component: Step2Workspace,
      description: 'Set up your workspace'
    },
    {
      id: 3,
      label: 'Project',
      component: Step3Project,
      description: 'Configure IoT project details'
    },
    {
      id: 4,
      label: 'Import',
      component: Step4TestSource,
      description: 'Import requirements (optional)'
    },
    {
      id: 5,
      label: 'Interaction Mode',
      component: Step5IotMode,
      description: 'Select how Robitest will interact with your device'
    },
    {
      id: 6,
      label: 'Lab Agent',
      component: Step6IotLabAgent,
      description: 'Connect your hardware test lab'
    },
    {
      id: 7,
      label: 'Device',
      component: Step7IotDevicePairing,
      description: 'Pair and register your device'
    },
    {
      id: 8,
      label: 'Setup',
      component: Step8IotInteractionSetup,
      description: 'Configure interaction settings'
    },
    {
      id: 9,
      label: 'Create Test',
      component: Step9IotCreateTest,
      description: 'Create your first hardware test'
    },

    //to move the flow as before remove the below 2 steps and uncomment the 4 steps listed in the end
        {
      id: 10,
      label: 'Run Test',
      component: Step12IotRun,
      description: 'Execute your first hardware test'
    },
    {
      id: 11,
      label: 'Summary',
      component: Step13IotSummary,
      description: 'View results and next steps'
    }

    //commeting the below steps temporarily to disable the step 10 and 11
    // {
    //   id: 10,
    //   label: 'Validation',
    //   component: Step10IotValidation,
    //   description: 'Set validation & evidence capture'
    // },
    // {
    //   id: 11,
    //   label: 'Generate',
    //   component: Step11IotGenerate,
    //   description: 'Generate hardware automation plan'
    // },
    // {
    //   id: 12,
    //   label: 'Run Test',
    //   component: Step12IotRun,
    //   description: 'Execute your first hardware test'
    // },
    // {
    //   id: 13,
    //   label: 'Summary',
    //   component: Step13IotSummary,
    //   description: 'View results and next steps'
    // }
  ],
  
  /**
   * Get step component by step number
   * @param {number} stepNum - Step number (1-13)
   * @param {Object} context - Current onboarding context
   * @returns {React.Component|null} Step component
   */
  getStepComponent(stepNum, context) {
    const step = this.steps.find(s => s.id === stepNum);
    return step ? step.component : null;
  },
  
  /**
   * Get next step number
   * @param {number} currentStep - Current step number
   * @param {Object} context - Current onboarding context
   * @returns {number} Next step number
   */
  getNextStep(currentStep, context) {
    // Simple sequential navigation for IoT flow
    const nextStep = currentStep + 1;
    return nextStep <= this.totalSteps ? nextStep : currentStep;
  },
  
  /**
   * Get previous step number
   * @param {number} currentStep - Current step number
   * @param {Object} context - Current onboarding context
   * @returns {number} Previous step number
   */
  getPrevStep(currentStep, context) {
    const prevStep = currentStep - 1;
    return prevStep >= 1 ? prevStep : currentStep;
  },
  
  /**
   * Check if user is allowed to access a specific step
   * Implements gating logic based on completed prerequisites
   * 
   * @param {number} stepNum - Step number to check
   * @param {Object} context - Current onboarding context
   * @returns {boolean} True if step is allowed
   */
  isStepAllowed(stepNum, context) {
    // Steps 1-4 are shared and always allowed if previous steps completed
    if (stepNum <= 4) {
      return stepNum === 1 || context.completedSteps?.includes(stepNum - 1);
    }
    
    const iot = context.iot || {};
    
    switch (stepNum) {
      case 5:
        // Interaction Mode: requires project created
        return !!context.project?.projectId;
        
      case 6:
        // Lab Agent: requires interaction mode selected
        return !!iot.interactionMode;
        
      case 7:
        // Device Pairing: requires lab agent connected
        return iot.labAgent?.connected === true;
        
      case 8:
        // Interaction Setup: requires device online
        return iot.device?.online === true;
        
      case 9:
        // Create Test: requires setup configured
        // Either commands (PROTOCOL) OR calibration (PHYSICAL) must be present
        return (
          (iot.setup?.commands?.length > 0) ||
          (iot.setup?.calibrationProfile !== null)
        );
       
      case 10: // Run Test (was step 12)
        return iot.automationJob?.suiteId !== null;

      case 11: // Summary (was step 13)
        return iot.testRun?.status === 'completed' || 
               iot.testRun?.status === 'failed';  
        
      // case 10:
      //   // Validation: requires at least one test case created
      //   return iot.testCaseIds?.length > 0;
        
      // case 11:
      //   // Generate: requires validations configured
      //   // Check both evidence settings and validation rules
      //   return (
      //     iot.evidence && 
      //     (iot.setup?.validations?.length > 0 || iot.evidence.captureLogs)
      //   );
        
      // case 12:
      //   // Run Test: requires automation suite generated
      //   return iot.automationJob?.suiteId !== null;
        
      // case 13:
      //   // Summary: requires test run completed
      //   return iot.testRun?.status === 'completed' || 
      //          iot.testRun?.status === 'failed'; // Show summary even if failed
        
      default:
        return true;
    }
  },
  
  /**
   * Get step by ID
   * @param {number} stepId - Step ID
   * @returns {Object|null} Step definition
   */
  getStep(stepId) {
    return this.steps.find(s => s.id === stepId) || null;
  },
  
  /**
   * Get gate message for a step
   * Provides user-friendly message when step is gated
   * 
   * @param {number} stepNum - Step number
   * @returns {string} Gate message
   */
  getGateMessage(stepNum) {
    const messages = {
      5: 'Complete project setup to continue',
      6: 'Select interaction mode to continue',
      7: 'Connect lab agent to continue',
      8: 'Device must be online to continue',
      9: 'Complete interaction setup to continue',
      10: 'Generate automation plan to continue',
      11: 'Run test to view summary'


      // 10: 'Create at least one test case to continue',
      // 11: 'Configure validation settings to continue',
      // 12: 'Generate automation plan to continue',
      // 13: 'Run test to view summary'
    };
    
    return messages[stepNum] || 'Complete previous steps to continue';
  }
};

export default iotFlow;